import { Link } from "react-router-dom";
import SEOHead from "@/lib/seo/SEOHead";

const PRIMARY = "#2D5BFF";
const ACCENT = "#65CE95";
const DARK = "#0F1311";

const Logo = ({ height = 40, tagline = false }) => (
  <div style={{ display: "inline-block" }}>
    <img
      src="https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/mlg09j9p_Ranktrust%20new%20logo.png"
      alt="RankTrust"
      style={{ height, width: "auto", display: "block" }}
    />
    {tagline && (
      <div
        style={{
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#4B5563",
          fontWeight: 700,
          textTransform: "uppercase",
          marginTop: 4,
          whiteSpace: "nowrap",
        }}
      >
        The AI Growth Intelligence Platform
      </div>
    )}
  </div>
);

function LegalLayout({ title, effectiveDate, children, testId }) {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Manrope, system-ui, sans-serif", color: DARK }} data-testid={testId}>
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
          <Link to="/" className="shrink-0">
            <Logo height={56} tagline />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/check"
              className="text-sm font-bold px-5 py-2.5 rounded-lg text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
              style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }}
            >
              Run Free Growth Diagnosis
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <Link to="/" className="text-xs font-bold tracking-wider uppercase text-zinc-500 hover:text-zinc-900 transition-colors">← Back to Home</Link>
        <h1 className="mt-6 text-4xl lg:text-5xl font-black tracking-tight">{title}</h1>
        <div className="mt-3 text-sm text-zinc-500 font-semibold">Effective Date: {effectiveDate}</div>

        <article className="mt-10 prose prose-zinc max-w-none">{children}</article>

        <div className="mt-16 pt-8 border-t border-zinc-100 text-sm text-zinc-500">
          Questions? Email{" "}
          <a href="mailto:support@ranktrust.io" style={{ color: PRIMARY }} className="font-semibold hover:underline">
            support@ranktrust.io
          </a>
        </div>
      </main>

      <footer className="py-10 border-t border-zinc-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 space-y-4 text-sm text-zinc-500">
          <div data-testid="footer-legal-entity">
            RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>© {new Date().getFullYear()} RankTrust · The AI Growth Intelligence Platform</div>
            <div className="flex gap-5 font-semibold">
              <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
              <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
              <a href="mailto:support@ranktrust.io" className="hover:text-zinc-900">Contact</a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        .prose h2 { font-size: 1.25rem; font-weight: 800; margin-top: 2.25rem; margin-bottom: 0.75rem; color: ${DARK}; letter-spacing: -0.01em; }
        .prose p { margin: 0.75rem 0; line-height: 1.7; color: #3F3F46; font-size: 0.975rem; }
        .prose ul { list-style: disc; padding-left: 1.25rem; margin: 0.75rem 0; }
        .prose li { margin: 0.35rem 0; line-height: 1.65; color: #3F3F46; font-size: 0.95rem; }
        .prose strong { color: ${DARK}; font-weight: 700; }
      `}</style>
    </div>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" effectiveDate="February 1, 2026" testId="terms-page">
      <SEOHead path="/terms" />
      <p>
        Welcome to RankTrust (&ldquo;RankTrust,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). RankTrust is a
        division of <strong>Card Service Warehouse, Inc.</strong>, a Delaware corporation, which is the legal entity
        that provides the RankTrust platform and services. RankTrust and Card Service Warehouse, Inc. operate
        under the same legal entity and EIN. References to &ldquo;RankTrust,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; and
        &ldquo;us&rdquo; in these Terms refer to Card Service Warehouse, Inc. doing business as RankTrust.
      </p>
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of RankTrust.io and related products,
        software, reports, APIs, dashboards, and services (collectively, the &ldquo;Platform&rdquo;). By accessing or
        using RankTrust, you agree to these Terms.
      </p>

      <h2>1. Services</h2>
      <p>
        RankTrust provides Google Business Profile visibility analysis, ranking intelligence, reporting, competitive
        analysis, AI-powered insights, optimization recommendations, and related tools.
      </p>
      <p>
        RankTrust provides informational analysis and recommendations only. We do not guarantee rankings, search
        placement, traffic levels, or business outcomes.
      </p>

      <h2>2. Eligibility</h2>
      <ul>
        <li>You must be at least 18 years old and legally able to enter agreements.</li>
        <li>You represent that all information provided is accurate.</li>
      </ul>

      <h2>3. Account Responsibilities</h2>
      <p>You are responsible for:</p>
      <ul>
        <li>Maintaining account security</li>
        <li>Protecting credentials</li>
        <li>Activities occurring under your account</li>
        <li>Promptly reporting unauthorized access</li>
      </ul>

      <h2>4. Subscriptions &amp; Billing</h2>
      <ul>
        <li>Paid plans renew automatically unless canceled.</li>
        <li>Fees are billed according to your selected plan.</li>
        <li>Failure to pay may result in suspension or termination.</li>
        <li>Unless required by law, payments are non-refundable.</li>
        <li>Trials may convert into paid subscriptions unless canceled before renewal.</li>
      </ul>

      <h2>5. Intellectual Property</h2>
      <p>
        All software, reports, scoring systems, algorithms, dashboards, branding, designs, content, and proprietary
        methods remain the property of RankTrust. Customers receive a limited, non-transferable license to use the
        Platform. You may not:
      </p>
      <ul>
        <li>Reverse engineer the Platform</li>
        <li>Resell access without authorization</li>
        <li>Copy scoring models</li>
        <li>Republish proprietary content</li>
      </ul>

      <h2>6. AI-Generated Content</h2>
      <ul>
        <li>RankTrust may use AI technologies to generate insights, reports, recommendations, or content.</li>
        <li>AI outputs may contain inaccuracies and should be independently reviewed.</li>
        <li>Users remain responsible for decisions based on generated outputs.</li>
      </ul>

      <h2>7. Third-Party Services</h2>
      <p>RankTrust may integrate with third parties including:</p>
      <ul>
        <li>Google Business Profile</li>
        <li>Analytics providers</li>
        <li>CRM systems</li>
        <li>AI providers</li>
        <li>Communication platforms</li>
      </ul>
      <p>We are not responsible for third-party systems or outages.</p>

      <h2>8. SMS and Text Message Communications</h2>
      <p>
        If you voluntarily provide a mobile number and check the SMS consent box on our Business Growth
        Assessment intake, you expressly agree to receive recurring text messages from Card Service
        Warehouse, Inc. (DBA: RankTrust) regarding your requested Business Growth Assessment, assessment
        results, visibility reports, appointment scheduling, account updates, review requests, and
        related customer service communications.
      </p>
      <p>
        Message frequency varies but will not exceed 5 messages per month. Message &amp; data rates may
        apply.
      </p>
      <p>
        You may unsubscribe at any time by replying STOP to any message. For assistance, reply HELP or
        contact RankTrust through the support information provided on our website.
      </p>
      <p>
        Consent to receive SMS messages is voluntary and is not a condition of purchase or of receiving
        any goods or services from RankTrust. You may use the Business Growth Assessment without
        entering a mobile number or consenting to SMS.
      </p>
      <p>
        Message delivery is subject to carrier availability, network conditions, and other factors
        outside RankTrust&rsquo;s control and is not guaranteed.
      </p>
      <p>
        Your consent applies only to the message categories described at the point of opt-in. We will
        not use SMS consent for materially different message categories without obtaining any
        additional consent required by applicable law or messaging-provider requirements.
      </p>

      <h2>9. White Label &amp; Agency Use</h2>
      <ul>
        <li>Agency customers remain responsible for representations made to their clients.</li>
        <li>RankTrust is not liable for agency-client disputes.</li>
        <li>White-label rights apply only to active subscriptions.</li>
      </ul>

      <h2>10. Disclaimers</h2>
      <p>THE PLATFORM IS PROVIDED &ldquo;AS IS.&rdquo; WE DISCLAIM ALL WARRANTIES INCLUDING:</p>
      <ul>
        <li>Merchantability</li>
        <li>Fitness for a particular purpose</li>
        <li>Non-infringement</li>
        <li>Uninterrupted operation</li>
        <li>Ranking guarantees</li>
      </ul>

      <h2>11. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, RankTrust shall not be liable for:</p>
      <ul>
        <li>Lost profits</li>
        <li>Revenue loss</li>
        <li>Indirect damages</li>
        <li>Business interruption</li>
        <li>Search ranking changes</li>
      </ul>
      <p>Total liability shall not exceed fees paid during the prior 12 months.</p>

      <h2>12. Termination</h2>
      <p>We may suspend or terminate access for:</p>
      <ul>
        <li>Terms violations</li>
        <li>Fraud</li>
        <li>Abuse</li>
        <li>Illegal activity</li>
      </ul>
      <p>Users may cancel at any time.</p>

      <h2>13. Governing Law</h2>
      <p>These Terms are governed by the laws of the State of Delaware, United States.</p>

      <h2>14. Contact</h2>
      <p>
        RankTrust, a division of Card Service Warehouse, Inc.<br />
        <a href="mailto:support@ranktrust.io" style={{ color: PRIMARY }}>support@ranktrust.io</a><br />
        <a href="https://ranktrust.io" style={{ color: PRIMARY }}>https://ranktrust.io</a>
      </p>
      <p style={{ fontSize: "0.9rem", color: "#52525B" }}>
        RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
      </p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="February 1, 2026" testId="privacy-page">
      <SEOHead path="/privacy" />
      <p>
        RankTrust (&ldquo;we,&rdquo; &ldquo;our,&rdquo; &ldquo;us&rdquo;) respects your privacy. RankTrust is a division of
        <strong> Card Service Warehouse, Inc.</strong>, a Delaware corporation, which is the legal entity that
        operates the RankTrust platform and is the data controller responsible for the information collected
        through it. RankTrust and Card Service Warehouse, Inc. operate under the same legal entity and EIN.
      </p>
      <p>
        This Privacy Policy explains how information is collected, used, and shared when you use our Platform.
      </p>

      <h2>1. Information We Collect</h2>
      <p><strong>Account Information:</strong></p>
      <ul>
        <li>Name</li>
        <li>Email</li>
        <li>Company information</li>
        <li>Billing details</li>
      </ul>
      <p><strong>Usage Data:</strong></p>
      <ul>
        <li>IP address</li>
        <li>Browser type</li>
        <li>Pages visited</li>
        <li>Device information</li>
        <li>Session activity</li>
      </ul>
      <p><strong>Business Data:</strong></p>
      <ul>
        <li>Google Business Profile information</li>
        <li>Business URLs</li>
        <li>Ranking reports</li>
        <li>Visibility analysis inputs</li>
        <li>Agency data</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>Provide reports</li>
        <li>Generate visibility analysis</li>
        <li>Deliver requested Growth Diagnoses</li>
        <li>Send text messages related to a user&rsquo;s request, including diagnosis delivery, appointment reminders, and service updates</li>
        <li>Operate the platform</li>
        <li>Process payments</li>
        <li>Improve services</li>
        <li>Provide support</li>
        <li>Prevent fraud</li>
        <li>Communicate updates</li>
      </ul>

      <h2>3. AI Processing</h2>
      <p>
        RankTrust may process submitted information using AI systems to generate reports, recommendations, and
        insights. AI-generated outputs may be reviewed for quality and improvement.
      </p>

      <h2>4. Cookies</h2>
      <p>We may use cookies and analytics technologies for:</p>
      <ul>
        <li>Performance</li>
        <li>Authentication</li>
        <li>Analytics</li>
        <li>Preferences</li>
        <li>Advertising</li>
      </ul>
      <p>Users may disable cookies in browser settings.</p>

      <h2>5. Sharing</h2>
      <p>We do not sell personal information. We may share data with:</p>
      <ul>
        <li>Payment processors</li>
        <li>Hosting providers</li>
        <li>Analytics providers</li>
        <li>AI providers</li>
        <li>Communication providers, including SMS, voice, and email delivery services</li>
        <li>Legal authorities when required</li>
      </ul>

      <h2>6. SMS Communications and Mobile Information</h2>
      <p>
        If you voluntarily provide a mobile number and check the SMS consent box on our Business Growth
        Assessment intake, Card Service Warehouse, Inc. (DBA: RankTrust) may use that number to send
        recurring text messages regarding your requested Business Growth Assessment, assessment results,
        visibility reports, appointment scheduling, account updates, review requests, and related
        customer service communications.
      </p>
      <p>
        Message frequency varies but will not exceed 5 messages per month. Message &amp; data rates may
        apply. You may unsubscribe at any time by replying STOP. For assistance, reply HELP.
      </p>
      <p>
        Consent to receive SMS messages is voluntary and is not a condition of purchase or of receiving
        any goods or services from RankTrust.
      </p>
      <p>
        We may retain records showing when and how SMS consent was provided, including the mobile number,
        request identifier, consent timestamp, consent-language version, and related technical records
        needed to document consent and comply with applicable requirements.
      </p>
      <p>
        We do not sell, rent, or share mobile numbers, SMS opt-in data, or SMS consent information with
        third parties or affiliates for their own marketing or promotional purposes.
      </p>
      <p>
        Mobile information may be disclosed only to vendors and service providers as reasonably necessary
        to operate RankTrust, deliver requested communications, maintain security, comply with legal
        obligations, or provide messaging infrastructure. Those providers may use the information only to
        perform services for RankTrust and not for their own independent marketing.
      </p>
      <p>
        All categories of information sharing described elsewhere in this Privacy Policy exclude SMS opt-in
        data and consent unless sharing is necessary to provide the messaging service or is required by
        law.
      </p>

      <h2>7. Data Retention</h2>
      <p>Data is retained while accounts remain active and as required by law. Users may request deletion.</p>

      <h2>8. Security</h2>
      <p>We use reasonable safeguards but cannot guarantee absolute security.</p>

      <h2>9. Your Rights</h2>
      <p>Depending on your location, you may request:</p>
      <ul>
        <li>Access</li>
        <li>Correction</li>
        <li>Deletion</li>
        <li>Export</li>
        <li>Restriction</li>
        <li>Objection</li>
      </ul>

      <h2>10. Children</h2>
      <p>RankTrust is not intended for children under 13.</p>

      <h2>11. Changes</h2>
      <p>We may update this policy periodically.</p>

      <h2>12. Contact</h2>
      <p>
        RankTrust, a division of Card Service Warehouse, Inc.<br />
        <a href="mailto:support@ranktrust.io" style={{ color: PRIMARY }}>support@ranktrust.io</a><br />
        <a href="https://ranktrust.io" style={{ color: PRIMARY }}>https://ranktrust.io</a>
      </p>
      <p style={{ fontSize: "0.9rem", color: "#52525B" }}>
        RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
      </p>
    </LegalLayout>
  );
}
